import { create } from 'zustand';
import { opsApi } from '../services/ops-api';
import type {
  OpsScorecard,
  HeatmapCell,
  RatingDistribution,
  NegativeFeedback,
  ProductPerformanceItem,
  OpsConfigData,
  OccupancyBreakdownRow,
  CapacityFormula,
  InternalControlData,
} from '../types/ops';
import { format, startOfWeek, endOfWeek } from 'date-fns';

const LS_CAPACITY_FORMULA_KEY = 'ops:capacityFormula';

function loadSavedFormula(): CapacityFormula {
  try {
    const v = localStorage.getItem(LS_CAPACITY_FORMULA_KEY);
    if (v === 'full' || v === 'subtractBlock') return v;
  } catch { /* SSR / private browsing */ }
  return 'subtractBlock';
}

type ActiveView = 'executive' | 'heatmap' | 'quality' | 'internal-control' | 'config';

/** Key encoding the params a view's data was fetched with */
type FetchKey = string;

interface OpsState {
  // Filters
  fromDate: string;
  toDate: string;
  excludeAtHome: boolean;
  activeView: ActiveView;

  // Data
  scorecards: OpsScorecard | null;
  checkinHeatmap: HeatmapCell[];
  sessionHeatmap: HeatmapCell[];
  ratingDistribution: RatingDistribution[];
  negativeFeedback: NegativeFeedback[];
  productPerformance: ProductPerformanceItem[];
  occupancyBreakdown: OccupancyBreakdownRow[];
  internalControl: InternalControlData | null;
  config: OpsConfigData | null;

  // The date range that was actually used for the last successful fetch
  reportFrom: string;
  reportTo: string;

  // Tracks which params each view was last fetched with
  fetchedKeys: Partial<Record<ActiveView, FetchKey>>;

  // Loading states
  isLoading: boolean;
  error: string | null;

  // Actions
  setDateRange: (from: string, to: string) => void;
  toggleAtHome: () => void;
  setActiveView: (view: ActiveView) => void;
  fetchDashboardData: (force?: boolean) => Promise<void>;
  fetchConfig: () => Promise<void>;
}

// Default to last week (Mon–Sun)
const now = new Date();
const thisMonday = startOfWeek(now, { weekStartsOn: 1 });
const lastMonday = new Date(thisMonday);
lastMonday.setDate(thisMonday.getDate() - 7);
const lastSunday = endOfWeek(lastMonday, { weekStartsOn: 1 });
const defaultFrom = format(lastMonday, 'yyyy-MM-dd');
const defaultTo = format(lastSunday, 'yyyy-MM-dd');

export const useOpsStore = create<OpsState>((set, get) => ({
  fromDate: defaultFrom,
  toDate: defaultTo,
  excludeAtHome: true,
  activeView: 'executive',

  scorecards: null,
  checkinHeatmap: [],
  sessionHeatmap: [],
  ratingDistribution: [],
  negativeFeedback: [],
  productPerformance: [],
  occupancyBreakdown: [],
  internalControl: null,
  config: { capacityFormula: loadSavedFormula() },
  reportFrom: defaultFrom,
  reportTo: defaultTo,
  fetchedKeys: {},

  isLoading: false,
  error: null,

  setDateRange: (from, to) => {
    set({ fromDate: from, toDate: to });
  },

  toggleAtHome: () => {
    set(state => ({ excludeAtHome: !state.excludeAtHome }));
  },

  setActiveView: (view) => {
    set({ activeView: view });
  },

  fetchDashboardData: async (force = false) => {
    const { fromDate, toDate, excludeAtHome, activeView, fetchedKeys } = get();
    const currentKey = `${fromDate}|${toDate}|${excludeAtHome}`;

    // Skip fetch if this view already has data for the same params
    if (!force && fetchedKeys[activeView] === currentKey) return;

    set({ isLoading: true, error: null });

    try {
      const params = { from: fromDate, to: toDate, excludeAtHome };

      if (activeView === 'executive') {
        const [scorecards, productPerformance, occupancyBreakdown, config] = await Promise.all([
          opsApi.getScorecards(params),
          opsApi.getProductPerformance(params),
          opsApi.getOccupancyBreakdown(params),
          opsApi.getConfig(),
        ]);
        set({ scorecards, productPerformance, occupancyBreakdown, config, isLoading: false });
      } else if (activeView === 'heatmap') {
        const [checkinHeatmap, sessionHeatmap] = await Promise.all([
          opsApi.getCheckinHeatmap(params),
          opsApi.getSessionHeatmap({ from: fromDate, to: toDate }),
        ]);
        set({ checkinHeatmap, sessionHeatmap, isLoading: false });
      } else if (activeView === 'quality') {
        const [ratingDistribution, negativeFeedback] = await Promise.all([
          opsApi.getRatingDistribution({ from: fromDate, to: toDate, excludeAtHome }),
          opsApi.getNegativeFeedback({ from: fromDate, to: toDate, excludeAtHome }),
        ]);
        set({ ratingDistribution, negativeFeedback, isLoading: false });
      } else if (activeView === 'internal-control') {
        const internalControl = await opsApi.getInternalControl(params);
        set({ internalControl, isLoading: false });
      } else if (activeView === 'config') {
        const config = await opsApi.getConfig();
        set({ config, isLoading: false });
      }

      // Mark this view as fetched and record the date range used
      set(state => ({
        fetchedKeys: { ...state.fetchedKeys, [activeView]: currentKey },
        reportFrom: fromDate,
        reportTo: toDate,
      }));
    } catch (error: any) {
      set({ error: error.message || 'Failed to fetch OPS data', isLoading: false });
    }
  },

  fetchConfig: async () => {
    try {
      const config = await opsApi.getConfig();
      set({ config });
    } catch (error: any) {
      set({ error: error.message || 'Failed to fetch config' });
    }
  },
}));
